import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/useAuth";
import { SunwaveLogo } from "../common/SunwaveLogo";

/**
 * Authenticated-app top bar — logo (links to /projects) + user menu.
 *
 * Rendered by ``AuthenticatedShell`` wrapping every workspace route.
 * The unauthenticated splash (`HomePage`) does NOT use this nav; it
 * has its own marketing-style header.
 */
export function TopNav() {
  const { user, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  const initials = userInitials(user?.email);
  return (
    <header
      data-testid="top-nav"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        background: "#ffffff",
        borderBottom: "1px solid #e5e7eb",
        padding: "0.5rem 32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
      }}
    >
      <Link
        to="/projects"
        data-testid="top-nav-home"
        aria-label="Back to projects"
        style={{ display: "inline-flex", alignItems: "center" }}
      >
        <SunwaveLogo height={32} />
      </Link>

      {user && (
        <div ref={menuRef} style={{ position: "relative" }}>
          <button
            type="button"
            data-testid="user-menu-trigger"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title={user.email}
            style={triggerBtnStyle}
          >
            {initials}
          </button>
          {menuOpen && (
            <div
              role="menu"
              data-testid="user-menu"
              style={menuBoxStyle}
            >
              <div style={menuHeaderStyle}>
                <div style={{ fontWeight: 600 }}>{user.email}</div>
                <div style={{ color: "#6b7280", fontSize: "0.75rem" }}>
                  {user.companyName}
                </div>
              </div>
              <button
                type="button"
                role="menuitem"
                data-testid="user-menu-sign-out"
                onClick={() => {
                  setMenuOpen(false);
                  void signOut().then(() => navigate("/"));
                }}
                style={menuItemStyle}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}

function userInitials(email?: string): string {
  if (!email) return "?";
  const at = email.indexOf("@");
  const local = at === -1 ? email : email.slice(0, at);
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

const triggerBtnStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: "50%",
  background: "#635bff",
  color: "#ffffff",
  border: "none",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: "0.85rem",
};

const menuBoxStyle: React.CSSProperties = {
  position: "absolute",
  right: 0,
  top: "calc(100% + 8px)",
  minWidth: 220,
  background: "#ffffff",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  padding: "0.5rem 0",
};

const menuHeaderStyle: React.CSSProperties = {
  padding: "0.5rem 1rem 0.75rem",
  borderBottom: "1px solid #f3f4f6",
  marginBottom: 4,
  fontSize: "0.85rem",
};

const menuItemStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "transparent",
  border: "none",
  padding: "0.55rem 1rem",
  cursor: "pointer",
  fontSize: "0.9rem",
};
